import { useState, useEffect } from "react";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import orderServices from "../../services/orders/orderservice";
import DeliveryAnalysisDatatable from "./DeliveryAnanlysis-Datatable";
import "./DeliveryAnalysis.css";
import ReportPageHeader from "../shared/ReportPageHeader";

// Modern color palette
const COLORS = {
  assigned: "#6366f1",
  delivered: "#10b981",
  undelivered: "#ef4444",
  cancelled: "#f59e0b",
};

const CHART_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981",
  "#3b82f6", "#14b8a6", "#06b6d4", "#f97316", "#ef4444",
];

// Custom hook for responsive behavior
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [matches, query]);

  return matches;
};

// Custom Tooltip for Bar Chart
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomBarTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          border: "none",
          borderRadius: "12px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          padding: "12px 16px",
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
          Days: {label}
        </p>
        <p style={{ margin: 0, color: "#667eea", fontWeight: 600 }}>
          Orders: {payload[0].value}
        </p>
      </div>
    );
  }
  return null;
};

// Gauge Chart Component (PieChartWithNeedle)
interface PieChartWithNeedleProps {
  value: number;
  color?: string;
}

const PieChartWithNeedle: React.FC<PieChartWithNeedleProps> = ({ 
  value, 
  color = "#10b981" 
}) => {
  const percent = Math.min(Math.max(value, 0), 1);
  const angle = 180 * percent;

  const data = [
    { name: "Progress", value: percent, fill: color },
    { name: "Remaining", value: 1 - percent, fill: "rgba(255,255,255,0.25)" },
  ];

  const RADIAN = Math.PI / 180;
  const cx = 50;
  const cy = 100;
  const needleLength = 35;

  const needleX = cx + needleLength * Math.cos((180 - angle) * RADIAN);
  const needleY = cy - needleLength * Math.sin((180 - angle) * RADIAN);

  return (
    <div className="gauge-container">
      <ResponsiveContainer>
        <PieChart>
          <Pie
            startAngle={180}
            endAngle={0}
            data={data}
            dataKey="value"
            cx="50%"
            cy="100%"
            innerRadius={35}
            outerRadius={50}
            stroke="none"
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.fill} />
            ))}
          </Pie>
          <g>
            <line
              x1="50%"
              y1="100%"
              x2={`${needleX}%`}
              y2={`${needleY}%`}
              stroke="rgba(0, 0, 0, 0.8)"
              strokeWidth={2}
            />
            <circle cx="50%" cy="100%" r={4} fill="rgba(0, 0, 0, 0.8)" />
          </g>
          <text
            x="50%"
            y="65%"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: "11px", fontWeight: "bold", fill: "#333" }}
          >
            {(percent * 100).toFixed(0)}%
          </text>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// Modern Stat Card Component
interface StatCardProps {
  title: string;
  count: number;
  icon: string;
  gradient: string;
  percentage?: number;
  trend?: "up" | "down";
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  count,
  icon,
  gradient,
  percentage,
  trend,
  onClick,
}) => {
  const getGaugeColor = () => {
    if (gradient.includes("#10b981")) return "#10b981";
    if (gradient.includes("#ef4444")) return "#f87171";
    if (gradient.includes("#f59e0b")) return "#fbbf24";
    if (gradient.includes("#667eea")) return "#818cf8";
    return "#10b981";
  };

  return (
    <div
      className="stat-card"
      style={{ background: gradient }}
      onClick={onClick}
    >
      <div className="stat-card-content">
        <div className="stat-header-simple">
          <span className="stat-title">{title}</span>
        </div>
        <div className="stat-value">{count}</div>
        {percentage !== undefined && (
          <div className="stat-gauge-bottom">
            <PieChartWithNeedle 
              value={percentage / 100} 
              color={getGaugeColor()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Updated Progress Bar Component - removed right scale value
interface ProgressBarProps {
  averageDays: number;
  baseline?: number;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  averageDays,
  baseline = 3,
}) => {
  const maxScale = Math.max(averageDays, baseline, 10);
  const avgPercent = (averageDays / maxScale) * 100;
  const baselinePercent = (baseline / maxScale) * 100;

  return (
    <div className="progress-container">
      <div className="progress-header">
        <span className="progress-label">Average Delivery Time</span>
        <span className="progress-value">{averageDays.toFixed(1)} days</span>
      </div>
      <div className="progress-bar-wrapper">
        {/* Green segment: 0 to baseline (3 days) */}
        <div
          className="progress-bar-segment progress-bar-green"
          style={{ width: `${baselinePercent}%` }}
        ></div>
        {/* Purple/gradient segment: baseline to average */}
        {averageDays > baseline && (
          <div
            className="progress-bar-segment progress-bar-purple"
            style={{ 
              width: `${avgPercent - baselinePercent}%`,
              left: `${baselinePercent}%`
            }}
          ></div>
        )}
        <div
          className="progress-baseline"
          style={{ left: `${baselinePercent}%` }}
        >
          <span className="baseline-label">Target: {baseline}d</span>
          <div className="baseline-marker"></div>
          <span className="baseline-value">{baseline}</span>
        </div>
      </div>
      <div className="progress-scale">
        {/* <span>0</span> */}
        
      </div>
    </div>
  );
};

interface UserOption {
  userID: number;
  name: string;
  primaryContactNbr: string;
}

interface UserResult {
  userID: number;
  detail: string;
}

const DeliveryAnalysis: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1024px)");

  const blankUserDetail: UserOption[] = [
    { userID: 0, name: "", primaryContactNbr: "" },
  ];

  const [orderUsers, setOrderUsers] = useState<UserOption[]>(blankUserDetail);
  const [orderUser, setOrderUser] = useState<UserResult[]>([]);
  const [dropdownValue, setDropdownValue] = useState<any>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const [counts, setCounts] = useState({
    homeDeliveryOrders: 0,
    assignedOrders: 0,
    unAssignedOrders: 0,
    deliveredOrders: 0,
    unDeliveredOrders: 0,
    cancelledOrders: 0,
    assignedCancelledOrders: 0,
  });

  const [delayedReasons, setDelayedReasons] = useState<
    { name: string; value: number }[]
  >([]);

  const [deliveryTimeStats, setDeliveryTimeStats] = useState<{
    averageDays: number;
    minDays: number;
    medianDays: number;
    maxDays: number;
    dayWiseCounts: { days: number; orders: number }[];
  }>({
    averageDays: 0,
    medianDays: 0,
    minDays: 0,
    maxDays: 0,
    dayWiseCounts: [],
  });

  const [selectedTable, setSelectedTable] = useState<
    | "ASSIGNED"
    | "DELIVERED"
    | "UNDELIVERED"
    | "DISTRIBUTION"
    | "CANCELLED"
    | null
  >(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setEndDate(today);
    setStartDate(thirtyDaysAgo);
  }, []);

  useEffect(() => {
    setOrderUser(
      orderUsers.map((user) => ({
        userID: user.userID,
        detail:
          user.userID !== 0 ? `${user.name}, ${user.primaryContactNbr}` : "",
      }))
    );
  }, [orderUsers]);

  useEffect(() => {
    if (startDate && endDate) fetchDeliveryAnalysis();
  }, [startDate, endDate, dropdownValue]);

  const handleDropdownChange = (e: any) => setDropdownValue(e.value);

  const handleDropdownFilter = (e: any) => {
    if (e.filter.length > 2) {
      orderServices.getDriversList(e.filter).then((response: UserOption[]) => {
        if (response.length > 0) setOrderUsers(response);
      });
    }
  };

  const fetchDeliveryAnalysis = async () => {
    setLoading(true);
    try {
      const payload = {
        startDate,
        endDate,
        executiveId: dropdownValue || null,
      };
      const data = await orderServices.getDeliveryAnalysis(payload);

      if (data) {
        setCounts(data);
        if (data.delayedOrders) {
          setDelayedReasons(
            data.delayedOrders.map((d: any) => ({
              name: d.name,
              value: d.count,
            }))
          );
        }
        if (data.deliveryStats) {
          setDeliveryTimeStats({
            averageDays: data.avgDays || 0,
            medianDays: data.medianDays || 0,
            minDays: data.minDays || 0,
            maxDays: data.maxDays || 0,
            dayWiseCounts: data.deliveryStats.map((d: any) => ({
              days: parseInt(d.deliveryTimeCategory, 10),
              orders: d.orderCount,
            })),
          });
        }
      }
    } catch (err) {
      console.error("Error fetching delivery analysis", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTable = async (
    type: "ASSIGNED" | "DELIVERED" | "UNDELIVERED" | "CANCELLED"
  ) => {
    setSelectedTable(type);
    setShowDialog(true);
    const payload = {
      startDate,
      endDate,
      executiveId: dropdownValue || null,
    };
    try {
      let res: any[] = [];
      if (type === "ASSIGNED")
        res = await orderServices.getAssignedOrdersDetails(payload);
      if (type === "DELIVERED")
        res = await orderServices.getDeliveredOrdersDetails(payload);
      if (type === "UNDELIVERED")
        res = await orderServices.getUndeliveredOrdersDetails(payload);
      if (type === "CANCELLED")
        res = await orderServices.getAssignedCancelledOrdersDetails(payload);

      const formatted = res.map((item: any) => ({
        orderNo: item.orderID,
        assignedDate: item.createdDate || item.assignedDate,
        deliveredDate: item.deliveredDate,
        assignedTo: item.driverName || item.assignedTo,
        deliveredBy: item.deliveredBy,
        customerName: item.customerName,
        customerNumber: item.primaryContactNbr || item.customerNumber,
        delayedReason: item.delayedReason ||item.delayedStatusName|| "-",
        
      }));
      setTableData(formatted);
    } catch (err) {
      console.error(`Failed to fetch ${type} orders`, err);
      setTableData([]);
    }
  };

  const handleBarClick = async (barData: any) => {
    const selectedDayValue = barData?.payload?.days;
    if (selectedDayValue === undefined) return;

    setSelectedDay(selectedDayValue);
    setSelectedTable("DISTRIBUTION");
    setShowDialog(true);

    const payload = {
      startDate,
      endDate,
      executiveId: dropdownValue || null,
      deliveryDays: selectedDayValue,
    };

    try {
      const res = await orderServices.getDeliveryAnalysis(payload);

      if (res && res.detailedOrders && res.detailedOrders.length > 0) {
        setTableData(
          res.detailedOrders.map((item: any) => ({
            orderNo: item.orderId,
            createdDate: item.orderCreatedDate,
            deliveredDate: item.orderDeliveredDate,
            driverName: item.driverName,
            customerName: item.customerName,
            customerNumber: item.customerNumber,
          }))
        );
      } else {
        setTableData([]);
      }
    } catch (err) {
      console.error("Error fetching orders by delivery days", err);
    }
  };

  const handleReset = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setDropdownValue(null);
    setEndDate(today);
    setStartDate(thirtyDaysAgo);
  };

  const totalOrders =
    counts.assignedOrders + counts.unAssignedOrders + counts.cancelledOrders;

  const assignedRatioData = [
    { name: "Assigned", value: counts.assignedOrders },
    { name: "Unassigned", value: counts.unAssignedOrders },
    { name: "Cancelled", value: counts.cancelledOrders },
  ];

  const performanceRatioData = [
    { name: "Delivered", value: counts.deliveredOrders },
    { name: "Undelivered", value: counts.unDeliveredOrders },
    { name: "Assigned Cancelled", value: counts.assignedCancelledOrders },
  ];

  return (
    <div className="delivery-analysis-container">
      <ReportPageHeader
        icon="pi-truck"
        title="Delivery Analysis"
        subtitle="Analyze assignment, delivery performance, and execution trends"
        filterLabel="Filter Delivery Analysis"
        actions={loading ? <ProgressSpinner style={{ width: "40px", height: "40px" }} /> : null}
      >
        <div className="filter-grid">
          <div className="filter-item">
            <label className="filter-label">Search Executive</label>
            <Dropdown
              options={orderUser || []}
              optionLabel="detail"
              optionValue="userID"
              value={dropdownValue}
              onChange={handleDropdownChange}
              onFilter={handleDropdownFilter}
              filter
              showClear
              placeholder="Search User / Mobile"
              className="w-full"
            />
          </div>
          <div className="filter-item">
            <label className="filter-label">Start Date</label>
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate(e.value)}
              dateFormat="dd/mm/yy"
              placeholder="Select Start Date"
              className="w-full"
              showIcon
            />
          </div>
          <div className="filter-item">
            <label className="filter-label">End Date</label>
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate(e.value)}
              dateFormat="dd/mm/yy"
              placeholder="Select End Date"
              className="w-full"
              showIcon
            />
          </div>
          <div className="filter-item">
            <label className="filter-label" style={{ opacity: 0 }}>Filter</label>
            <Button
              label="Filter"
              icon="pi pi-filter"
              className="p-button-success w-full"
              onClick={fetchDeliveryAnalysis}
            />
          </div>
          <div className="filter-item">
            <label className="filter-label" style={{ opacity: 0 }}>Reset</label>
            <Button
              label="Reset"
              icon="pi pi-refresh"
              className="p-button-success w-full"
              onClick={handleReset}
            />
          </div>
        </div>
      </ReportPageHeader>

      <div className="stats-grid">
        <StatCard
          title="Assigned Orders"
          count={counts.assignedOrders}
          icon="pi-check-circle"
          gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          percentage={totalOrders > 0 ? (counts.assignedOrders / totalOrders) * 100 : 0}
          trend="up"
          onClick={() => handleOpenTable("ASSIGNED")}
        />
        <StatCard
          title="Delivered Orders"
          count={counts.deliveredOrders}
          icon="pi-verified"
          gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
          percentage={
            counts.assignedOrders > 0
              ? (counts.deliveredOrders / counts.assignedOrders) * 100
              : 0
          }
          trend="up"
          onClick={() => handleOpenTable("DELIVERED")}
        />
        <StatCard
          title="Undelivered Orders"
          count={counts.unDeliveredOrders}
          icon="pi-exclamation-circle"
          gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
          percentage={
            counts.assignedOrders > 0
              ? (counts.unDeliveredOrders / counts.assignedOrders) * 100
              : 0
          }
          trend="down"
          onClick={() => handleOpenTable("UNDELIVERED")}
        />
        <StatCard
          title="assigned cancelled orders"
          count={counts.assignedCancelledOrders}
          icon="pi-times-circle"
          gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
          percentage={
            counts.assignedOrders > 0
              ? (counts.assignedCancelledOrders / counts.assignedOrders) * 100
              : 0
          }
          trend="down"
          onClick={() => handleOpenTable("CANCELLED")}
        />
      </div>

      <div className="charts-grid">
        <div className="chart-card chart-card-large">
          <Card>
            <h3 className="chart-title">Delivery Time Distribution</h3>
            {loading ? (
              <div className="chart-loading">
                <ProgressSpinner />
              </div>
            ) : (
              <div className="chart-content">
                <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                  <BarChart
                    data={deliveryTimeStats.dayWiseCounts}
                    margin={{
                      top: 20,
                      right: isMobile ? 10 : 30,
                      left: isMobile ? -20 : 0,
                      bottom: 20,
                    }}
                  >
                    <defs>
                      <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#667eea" stopOpacity={1} />
                        <stop offset="100%" stopColor="#764ba2" stopOpacity={0.8} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="days"
                      fontSize={isMobile ? 11 : 13}
                      stroke="#6b7280"
                      label={
                        !isMobile
                          ? {
                              value: "Days to Deliver",
                              position: "insideBottom",
                              offset: -10,
                              style: { fontSize: 14, fontWeight: 600, fill: "#374151" },
                            }
                          : undefined
                      }
                    />
                    <YAxis
                      fontSize={isMobile ? 11 : 13}
                      stroke="#6b7280"
                      label={
                        !isMobile
                          ? {
                              value: "Orders",
                              angle: -90,
                              position: "insideLeft",
                              style: { fontSize: 14, fontWeight: 600, fill: "#374151" },
                            }
                          : undefined
                      }
                    />
                    <Tooltip
                      content={<CustomBarTooltip />}
                      cursor={{ fill: "rgba(102, 126, 234, 0.1)" }}
                    />
                    <Bar
                      dataKey="orders"
                      fill="url(#colorBar)"
                      radius={[8, 8, 0, 0]}
                      onClick={(data) => handleBarClick(data)}
                    />
                  </BarChart>
                </ResponsiveContainer>

                <div className="progress-section">
                  <ProgressBar
                    averageDays={deliveryTimeStats.averageDays}
                    baseline={3}
                  />
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="chart-card">
          <Card>
            <h3 className="chart-title">Performance Analysis</h3>
            {loading ? (
              <div className="chart-loading">
                <ProgressSpinner />
              </div>
            ) : (
              <div className="chart-content chart-content-centered">
                <ResponsiveContainer width="100%" height={isMobile ? 320 : 520}>
                  <PieChart>
                    <Pie
                      data={performanceRatioData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 70 : 90}
                      label={!isMobile ? (entry) => `${entry.value}` : false}
                      labelLine={!isMobile}
                    >
                      {performanceRatioData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i]} />
                      ))}
                    </Pie>
                    {!dropdownValue && (
                      <Pie
                        data={assignedRatioData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 110 : 140}
                        outerRadius={isMobile ? 140 : 180}
                        label={!isMobile ? (entry) => `${entry.value}` : false}
                        labelLine={!isMobile}
                      >
                        {assignedRatioData.map((_, i) => (
                          <Cell
                            key={i}
                            fill={CHART_COLORS[(i + 3) % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                    )}
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "none",
                        borderRadius: "12px",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend
                      wrapperStyle={{
                        fontSize: isMobile ? "11px" : "13px",
                        paddingTop: "20px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        <div className="chart-card">
          <Card>
            <h3 className="chart-title">Delayed Orders Reasons</h3>
            {loading ? (
              <div className="chart-loading">
                <ProgressSpinner />
              </div>
            ) : (
              <div className="chart-content chart-content-centered">
                <ResponsiveContainer width="100%" height={isMobile ? 320 : 520}>
                  <PieChart>
                    <Pie
                      data={delayedReasons}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={isMobile ? 70 : 90}
                      outerRadius={isMobile ? 100 : 140}
                      label={!isMobile ? (entry) => `${entry.value}` : false}
                      labelLine={!isMobile}
                    >
                      {delayedReasons.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "none",
                        borderRadius: "12px",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend
                      wrapperStyle={{
                        fontSize: isMobile ? "11px" : "13px",
                        paddingTop: "20px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      </div>

      <Dialog
        header={
          selectedTable === "DISTRIBUTION" && selectedDay !== null
            ? `Orders Delivered in ${selectedDay} Days`
            : `${selectedTable || ""} Orders`
        }
        visible={showDialog}
        style={{ width: isMobile ? "95vw" : "85vw" }}
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
        onHide={() => setShowDialog(false)}
        maximizable
      >
        <DeliveryAnalysisDatatable
          selectedTable={selectedTable}
          data={tableData}
        />
      </Dialog>
    </div>
  );
};
export default DeliveryAnalysis;
